import { businessShape, type BusinessShape } from "@teamna/shared";
import { useMemo } from "react";
import { useBusinesses } from "./use-member.ts";
import { useActiveBusiness } from "./use-owner.ts";

/**
 * The shape of a business (migration 0069): who books (`bookingFor`) and how classes fill
 * (`classFill`). Screens decide what to show from it, never from the sport. The words still come
 * from `useLabels()`.
 *
 * Every role reads it: the shape comes on each row of `GET /v1/me/businesses`, the query the app
 * already holds, so this makes no extra request. Without a `businessId` it reads the business of
 * the owner office. Until the list has loaded, and for an older API, it answers with the start
 * shape of the sport (or of `other`).
 */
export function useShape(businessId?: string | null): {
  shape: BusinessShape;
  /** Members book for their children (a family). */
  forChildren: boolean;
  /** Each participant stays in one group. */
  fixedGroups: boolean;
  /** The business has loaded. Until then the shape is the start shape, so a guard waits for this. */
  ready: boolean;
} {
  const { business } = useActiveBusiness();
  const { data } = useBusinesses();
  const target = businessId ? (data ?? []).find((row) => row.businessId === businessId) : business;
  const bookingFor = target?.bookingFor;
  const classFill = target?.classFill;
  const sport = target?.sport;
  const ready = target !== undefined && target !== null;
  return useMemo(() => {
    const shape = businessShape({ bookingFor, classFill, sport });
    return { shape, forChildren: shape.bookingFor === "children", fixedGroups: shape.classFill === "fixed_group", ready };
  }, [bookingFor, classFill, sport, ready]);
}
